import { Card, Stack, Typography } from '@healthinal/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { request } from '../../../../api/axios';
import { getGetPatientByIdQueryKey } from '../../../../api/generated/patient-controller/patient-controller';
import {
  getPersonById,
  IPersonDetails,
  IPersonListItem,
  searchPersonsForAutocomplete,
} from '../../../../api/generated/person-controller/person-controller';
import { SingleLineFormControl } from '../../../../components/form/SingeLineFormComponents';
import { WarningConfirmationModal } from '../../../components/WarningConfirmationModal';
import { GenericAutocomplete } from '../../../components/GenericAutocomplete';

interface FormValues {
  selectedPerson: IPersonListItem | null;
}

interface Props {
  patientId: string;
  personId?: string | null;
  caseNumber: string;
  title?: string;
  onPersonSelected?: (person: IPersonListItem | null) => void;
  onCompleted?: () => void;
  onPersonSynced?: () => void;
}

function buildPatientPutPayload(patientId: string, caseNumber: string, person: IPersonDetails) {
  const address = person.address as unknown;

  return {
    id: patientId,
    personId: person.id,
    caseNumber,
    salutation: person.salutation ?? undefined,
    firstName: person.firstName,
    lastName: person.lastName,
    birthDate: person.birthDate,
    gender: person.gender,
    livingStatus: person.livingStatus ?? null,
    note: person.note ?? undefined,
    email: person.email ?? undefined,
    mobilePhoneNumber: person.mobilePhoneNumber ?? undefined,
    privatePhoneNumber: person.privatePhoneNumber ?? undefined,
    workPhoneNumber: person.workPhoneNumber ?? undefined,
    nationality: person.nationality ?? undefined,
    origin: person.origin ?? undefined,
    arrival: person.arrival ?? undefined,
    asylumNumber: person.asylumNumber ?? undefined,
    zemisNumber: person.zemisNumber ?? undefined,
    additionalInformation: person.additionalInformation ?? undefined,
    street: person.street ?? undefined,
    languages: person.languages ?? [],
    residencyPermit: person.residencyPermit ?? undefined,
    address: address ?? null,
    contact: person.contact ?? null,
    bezugspersonen: person.bezugspersonen ?? [],
    bezugspersonIds: person.bezugspersonIds ?? [],
  };
}

export function SelectPatientCard({
  patientId,
  personId,
  caseNumber,
  title = 'Person auswählen',
  onPersonSelected,
  onCompleted,
  onPersonSynced,
}: Props) {
  const queryClient = useQueryClient();
  const [pendingPerson, setPendingPerson] = useState<IPersonListItem | null>(null);
  const [isChangePersonModalOpen, setIsChangePersonModalOpen] = useState(false);

  const { control, reset, setValue } = useForm<FormValues>({
    defaultValues: {
      selectedPerson: null,
    },
  });

  // Tracks whether initial preselection has been completed
  const hasHydratedInitialValueRef = useRef(false);

  const updatePatientMutation = useMutation({
    mutationFn: async (payload: unknown) => {
      return request({
        url: '/api/patients',
        method: 'PUT',
        data: payload,
      });
    },
    onSuccess: async () => {
      toast.success('Patient erfolgreich aktualisiert');

      await queryClient.invalidateQueries({
        queryKey: getGetPatientByIdQueryKey(patientId),
      });

      await queryClient.refetchQueries({
        queryKey: getGetPatientByIdQueryKey(patientId),
      });

      onCompleted?.();
      onPersonSynced?.();
    },
    onError: () => {
      toast.error('Patient konnte nicht aktualisiert werden');
    },
  });

  // Initial hydration from existing personId
  useEffect(() => {
    if (hasHydratedInitialValueRef.current) return;

    if (!personId) {
      setValue('selectedPerson', null, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      hasHydratedInitialValueRef.current = true;
      return;
    }

    let isMounted = true;

    void getPersonById(personId)
      .then((person) => {
        if (!isMounted) return;

        setValue(
          'selectedPerson',
          {
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            birthDate: person.birthDate,
            gender: person.gender,
            created: person.created,
            phone: person.mobilePhoneNumber ?? person.privatePhoneNumber ?? person.workPhoneNumber ?? null,
            email: person.email ?? null,
          },
          {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          },
        );

        hasHydratedInitialValueRef.current = true;
      })
      .catch(() => {
        if (!isMounted) return;
        hasHydratedInitialValueRef.current = true;
      });

    return () => {
      isMounted = false;
    };
  }, [personId, setValue]);

  const applyPersonChange = async (person: IPersonListItem) => {
    const personDetails = await getPersonById(person.id);
    await updatePatientMutation.mutateAsync(buildPatientPutPayload(patientId, caseNumber, personDetails));
    reset({ selectedPerson: person });
  };

  const handlePersonChange = async (
    person: IPersonListItem | null,
    onFieldChange: (value: IPersonListItem | null) => void,
  ) => {
    // Skip unnecessary PUT if the same person is selected again.
    if ((person?.id ?? null) === (personId ?? null)) {
      onFieldChange(person);
      onPersonSelected?.(person);
      reset({ selectedPerson: person });
      return;
    }

    if (!person?.id) {
      return;
    }

    // Changing an existing Indexpatient requires confirmation.
    if (personId) {
      setPendingPerson(person);
      setIsChangePersonModalOpen(true);
      return;
    }

    // First-time assignment: apply immediately.
    onFieldChange(person);
    onPersonSelected?.(person);

    try {
      await applyPersonChange(person);
    } catch {
      toast.error('Persondaten konnten nicht geladen werden');
    }
  };

  const handleConfirmPersonChange = async () => {
    if (!pendingPerson) {
      return;
    }

    try {
      await applyPersonChange(pendingPerson);
      setIsChangePersonModalOpen(false);
      setPendingPerson(null);
    } catch {
      toast.error('Persondaten konnten nicht geladen werden');
    }
  };

  const handleCancelPersonChange = () => {
    setIsChangePersonModalOpen(false);
    setPendingPerson(null);
  };

  return (
    <Card>
      <Stack>
        <Controller
          name="selectedPerson"
          control={control}
          render={({ field, fieldState }) => (
            <SingleLineFormControl<FormValues> label={title} fieldState={fieldState}>
              <GenericAutocomplete<IPersonListItem>
                value={field.value}
                onChange={(person) => {
                  void handlePersonChange(person, field.onChange);
                }}
                fetchOptions={async ({ search, page, pageSize, signal }) => {
                  return searchPersonsForAutocomplete(search, {
                    page,
                    size: pageSize,
                    signal,
                  });
                }}
                getOptionLabel={(option) => `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim()}
                getOptionKey={(option) => option.id}
                getSearchText={(option) =>
                  `${option.firstName ?? ''} ${option.lastName ?? ''} ${option.email ?? ''} ${option.phone ?? ''}`
                }
                isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                enableSearch
                enableLazyLoad
                variant="outlined"
                noOptionsText="Keine Personen gefunden"
              />
            </SingleLineFormControl>
          )}
        />

        {updatePatientMutation.isPending && <Typography level="body-sm">Patient wird aktualisiert…</Typography>}
      </Stack>

      <WarningConfirmationModal
        open={isChangePersonModalOpen}
        title="Person ändern"
        loading={updatePatientMutation.isPending}
        onCancel={handleCancelPersonChange}
        onConfirm={() => void handleConfirmPersonChange()}>
        <Typography>Bist du sicher, dass du die Person ändern möchtest?</Typography>
      </WarningConfirmationModal>
    </Card>
  );
}
